import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import CampoBusca from '../componentes/CampoBusca';
import Ordenacao from '../componentes/Ordenacao';
import SeletorCliente from '../componentes/SeletorCliente';
import SeletorVeiculo from '../componentes/SeletorVeiculo';
import type { CarroEscolhido } from '../componentes/SeletorVeiculo';
import { carroDaVenda, carroParaRpc } from '../lib/carro';
import PainelDevolucao from '../componentes/PainelDevolucao';
import MiniaturaDaPeca from '../componentes/MiniaturaDaPeca';
import MudarVendedorDaVenda from '../componentes/MudarVendedorDaVenda';
import EmitirFiado from '../componentes/EmitirFiado';
import { Link, useSearchParams } from 'react-router';
import {
  cancelarEntrega, cancelarVenda, corrigirVenda, definirEntrega, devolucoesDaVenda,
  editarVenda, enfileirarImpressao, fotosDasPecas, itensDaVendaParaGerencia, lerItensDaVenda,
  lerVendas, marcarEntregaFeita,
  perfis, quitacaoDaVenda, trocarVendedorDasVendas, vendaPeloCodigo, versoesDaVenda,
  type VendaDetalhada,
} from '../lib/consultas';
import { baixarCsv, paraCsv } from '../lib/csv';
import { brl, dataHoraBR, hojeISO, paraNumero, qtd } from '../lib/formato';
import { codigoDaPeca } from '../lib/codigoDaPeca';
import {
  imprimirAqui, montarNotinhaDeEntrega, montarRecibo, textoDoCarro, vendaParaPapel,
  type VendaDoRecibo,
} from '../lib/reciboDoc';
import { mensagemDeErro } from '../lib/supabase';
import {
  podeCorrigirVenda, podeGerenciarVendas, podeTrocarVendedorDaVenda, podeVerFaturamento,
  podeVerVendasDeTodos, useSessao,
} from '../lib/sessao';
import { ehItemAvulso, nomeDoItemVendido } from '../lib/coringa';
import { casaBusca, porData, porNumero, porTexto } from '../lib/listas';
import { casaFormas, descreverPagamento } from '../lib/pagamentos';
import {
  CANAIS_DE_VENDA, FORMAS_DE_PAGAMENTO, rotuloDaForma, rotuloDoCanalDeVenda,
} from '../lib/rotulos';
import type { PaymentMethod, Profile, SaleChannel } from '@core/types';

// Tela 11 — a MESMA tela serve os três papéis, porque quem recorta os dados é
// a RLS, não o React:
//   vendedor/gerente → sel_sales devolve só as vendas de quem está logado
//   dono             → devolve todas, e aparece o filtro por vendedor
//
// Duas telas quase iguais divergiriam na primeira correção. Uma só, com a
// permissão vinda do banco, não tem como divergir.
//
// SEM AGREGADO PARA A EQUIPE (28/09/2026). O dono: "funcionário não pode ver
// faturamento, nem quanto vende" — e gerente é funcionário. Então os três
// cartões do topo (Vendido, Vendas, Ticket médio) e o filtro por vendedor são
// só dele.
//
// O que FICA para quem vendeu, e é o coração da decisão: a lista das vendas
// dele, o VALOR DE CADA UMA na linha, o subtotal de cada peça no detalhe e o
// recibo que ele imprime para o cliente. Ninguém atende sem saber quanto
// cobrou; o que o dono tirou foi a SOMA, não a venda.
//
// E O QUE ENTROU NO MESMO DIA: a busca por CÓDIGO (BuscaPeloCodigo, no fim
// deste arquivo), só para o gerente. Ele perdeu a VISTA das vendas dos
// colegas, não o trabalho — cancelar, corrigir, devolver peça e cobrar fiado
// de venda que não é dele segue sendo dele. Sem essa caixa, a lista não tem a
// venda e o único caminho seria chamar o dono no meio do atendimento.
//
// FILTRO SERVIDOR vs. FILTRO TELA (01/09/2026, a pedido do dono): o PERÍODO, o
// vendedor e o "incluir canceladas" recortam no BANCO — são eles que decidem
// quais linhas descem pela rede. Busca, forma, canal e ordem recortam na
// TELA, sobre essas linhas.
//
// Até 11/09/2026 desciam NO MÁXIMO 500, e os cartões somavam só essas: num
// período de mais de duas semanas o "Vendido" de "Todos" ficava menor que a
// soma dos vendedores, porque nenhum vendedor sozinho batia no teto. Hoje
// listarVendas lê em páginas (lib/consultas) e desce o período inteiro. A divisão não é capricho: filtro de
// servidor custa uma ida à rede a cada tecla digitada, e busca que só responde
// depois da rede é busca que ninguém usa no balcão.
//
// E OS CARTÕES SEGUEM O FILTRO, de propósito: filtrar por "pix" e ver quanto
// entrou em pix é a pergunta que o dono faz. Cartão que ignora o filtro
// responde a uma pergunta que ninguém fez. (Hoje esses cartões existem só para
// ele — ver acima.)

type OrdemVenda = 'recente' | 'antiga' | 'maior' | 'menor' | 'cliente' | 'vendedor';

// --- O CSV das vendas que estão na tela --------------------------------------
//
// Função pura, e fora do componente desde 28/09/2026: no dia em que a soma saiu
// da tela da equipe, o que precisava ficar PROVADO é que a linha da venda
// própria continua trazendo o valor dela. Cada linha é UMA venda — nunca um
// total —, e é isso que o vendedor precisa quando confere o dia dele com o
// papel na mão.

export const COLUNAS_DO_CSV_DE_VENDAS = [
  'Data/hora', 'Código', 'Vendedor', 'Cliente', 'Carro', 'Forma', 'Canal', 'Total', 'Situação',
];

export function linhasDoCsvDeVendas(vendas: VendaDetalhada[]): Array<Array<string | number>> {
  return vendas.map((v) => [
    dataHoraBR(v.vendida_em), v.codigo ?? '', v.vendedor ?? '', v.cliente ?? '',
    textoDoCarro(v.veiculo_montadora, v.veiculo_modelo, v.veiculo_ano) ?? '',
    descreverPagamento(v.forma_pagamento, v.sale_payments), rotuloDoCanalDeVenda(v.canal),
    // Vírgula decimal: o arquivo é para abrir no Excel em português.
    Number(v.total).toFixed(2).replace('.', ','),
    v.cancelada_em ? 'cancelada' : 'válida',
  ]);
}

const ORDENS_VENDA = [
  { id: 'recente', rotulo: 'Mais recente' },
  { id: 'antiga', rotulo: 'Mais antiga' },
  { id: 'maior', rotulo: 'Maior valor' },
  { id: 'menor', rotulo: 'Menor valor' },
  { id: 'cliente', rotulo: 'Cliente (A–Z)' },
  { id: 'vendedor', rotulo: 'Vendedor (A–Z)' },
] as const satisfies ReadonlyArray<{ id: OrdemVenda; rotulo: string }>;

// O começo do sistema (13/08/2026, no mês de agosto): "desde o começo" no
// painel de emitir o fiado abre a lista até aqui.
const INICIO_DO_SISTEMA = '2026-08-01';

export default function Vendas() {
  const { papel, perfil } = useSessao();
  const cliente = useQueryClient();
  // Gerência: cancelar, devolver/trocar, corrigir digitação — segue de gerente
  // e dono (o gerente cobra fiado e conserta venda pelo código).
  const gerencia = podeGerenciarVendas(papel);
  // A SOMA do período — só o dono (28/09/2026).
  const verFaturamento = podeVerFaturamento(papel);
  // A LISTA de todo mundo — gerente e dono (07/10/2026). São DOIS predicados
  // de propósito, e é a fronteira que o dono desenhou: ver cada venda, sim;
  // ver a soma, não. Trocar um pelo outro aqui devolve o faturamento ao
  // gerente — por isso o cartão de dinheiro lá embaixo continua em
  // `verFaturamento`, e só a lista, o filtro e o título olham para este.
  const verTodas = podeVerVendasDeTodos(papel);

  // Vir de fora apontando para UMA venda (?venda=<id>&dia=<AAAA-MM-DD>): é o
  // link da tela do Fiado. Sem isto ele caía na lista dos últimos 7 dias, e a
  // venda de 11 dias atrás simplesmente não estava lá — foi assim que "devolver
  // peça no fiado" virou impossível de achar (relato do dono, 28/08).
  const [params] = useSearchParams();
  const vendaAlvo = params.get('venda');
  const diaAlvo = params.get('dia');

  const [de, setDe] = useState(() => {
    if (diaAlvo) return diaAlvo;
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return hojeISO(d);
  });
  const [ate, setAte] = useState(() => diaAlvo ?? hojeISO());
  const [vendedorId, setVendedorId] = useState('');
  const [incluirCanceladas, setIncluirCanceladas] = useState(false);
  const [busca, setBusca] = useState('');
  // VÁRIAS FORMAS DE UMA VEZ (pedido do dono, 14/09/2026): "dinheiro e pix"
  // é uma pergunta só. Lista vazia = todas. A venda DIVIDIDA (075) casa se
  // qualquer parte casar — ver casaFormas em lib/pagamentos.
  const [formas, setFormas] = useState<PaymentMethod[]>([]);
  const [canal, setCanal] = useState<SaleChannel | 'todos'>('todos');
  const [ordem, setOrdem] = useState<OrdemVenda>('recente');
  // Já abre a sanfona da venda apontada — quem clicou no link quer ESTA venda.
  const [aberta, setAberta] = useState<string | null>(vendaAlvo);
  // O recado de "N vendas passaram para Fulano" fica até o próximo filtro.
  const [recadoDaTroca, setRecadoDaTroca] = useState<string | null>(null);
  // O painel "Emitir" do fiado (14/10/2026) — só existe com o filtro Fiado.
  const [emitindoFiado, setEmitindoFiado] = useState(false);

  const periodo = useMemo(() => {
    const inicio = new Date(`${de}T00:00:00`);
    const fim = new Date(`${ate}T00:00:00`);
    fim.setDate(fim.getDate() + 1);
    return { de: inicio.toISOString(), ate: fim.toISOString() };
  }, [de, ate]);

  // A porta muda com o papel: o gerente entra pela RPC da gerência (a policy
  // de `sales` continua dando a cada um só as vendas dele). A porta entra na
  // CHAVE do cache porque trocar de login no mesmo aparelho não pode
  // reaproveitar a lista do outro.
  const porta = verTodas && !verFaturamento ? 'gerencia' : 'tabela';
  const vendas = useQuery({
    queryKey: ['vendas', porta, periodo.de, periodo.ate, vendedorId, incluirCanceladas],
    queryFn: () => lerVendas(verTodas, verFaturamento)({
      de: periodo.de, ate: periodo.ate,
      vendedorId: vendedorId || null,
      incluirCanceladas,
    }),
  });

  // A lista da equipe serve ao filtro "por vendedor" — do dono desde sempre e
  // do gerente desde 07/10: é ele que torna "as vendas do Alex de hoje" um
  // gesto, que é o que o dono chamou de "de forma fácil". Para o vendedor a
  // consulta nem sai.
  const equipe = useQuery({
    queryKey: ['perfis'], queryFn: perfis,
    enabled: verTodas, staleTime: 10 * 60_000,
  });

  const linhas = useMemo(() => {
    const filtradas = (vendas.data ?? []).filter((v) => (
      casaFormas(v.forma_pagamento, v.sale_payments, formas)
      && (canal === 'todos' || v.canal === canal)
      // O código é o que o cliente traz no papel; o resto é como o balcão
      // lembra da venda ("aquela do Pedro", "a da Kombi na observação").
      // E AS PEÇAS (11/09/2026): "dot4" tem de achar a venda do fluido DOT 4.
      // Quem liga perguntando diz a peça, não o número da venda.
      // E O CARRO (073): "kombi" acha as vendas da Kombi — é a pergunta que o
      // balcão faz quando o cliente volta e não lembra do número.
      && casaBusca(busca, v.codigo, v.cliente, v.vendedor, v.observacao, v.veiculo_modelo,
                   v.sale_items.map((i) => `${nomeDoItemVendido(i)} ${i.products?.sku ?? ''} ${i.codigo_avulso ?? ''}`).join(' '))
    ));
    return [...filtradas].sort((a, b) => {
      if (ordem === 'antiga') return porData(a.vendida_em, b.vendida_em, 'asc');
      if (ordem === 'maior') return porNumero(Number(a.total), Number(b.total), 'desc');
      if (ordem === 'menor') return porNumero(Number(a.total), Number(b.total), 'asc');
      // Empate de nome cai na data: dentro do mesmo cliente, a mais nova em
      // cima — senão a ordem entre elas fica ao sabor do que o banco devolveu.
      if (ordem === 'cliente') {
        return porTexto(a.cliente, b.cliente) || porData(a.vendida_em, b.vendida_em);
      }
      if (ordem === 'vendedor') {
        return porTexto(a.vendedor, b.vendedor) || porData(a.vendida_em, b.vendida_em);
      }
      return porData(a.vendida_em, b.vendida_em);
    });
  }, [vendas.data, busca, formas, canal, ordem]);

  const totais = useMemo(() => totaisDeVendas(linhas, verFaturamento), [linhas, verFaturamento]);

  const filtrando = busca.trim() !== '' || formas.length > 0 || canal !== 'todos';
  const limparFiltros = () => { setBusca(''); setFormas([]); setCanal('todos'); };

  // O CSV sai do que está NA TELA, não do período inteiro: quem filtrou por
  // pix e exportou quer o pix. Por isso o nome do arquivo avisa que é recorte.
  const exportar = () => {
    baixarCsv(
      `vendas-${de}-a-${ate}${filtrando ? '-filtrado' : ''}.csv`,
      paraCsv(COLUNAS_DO_CSV_DE_VENDAS, linhasDoCsvDeVendas(linhas)),
    );
  };

  return (
    <Layout titulo={verTodas ? 'Vendas' : 'Minhas vendas'}>
      <div className="space-y-4">
        <section className="cartao space-y-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="rotulo" htmlFor="de">De</label>
              <input id="de" type="date" className="campo" value={de}
                     onChange={(e) => setDe(e.target.value)} />
            </div>
            <div>
              <label className="rotulo" htmlFor="ate">Até</label>
              <input id="ate" type="date" className="campo" value={ate}
                     onChange={(e) => setAte(e.target.value)} />
            </div>
          </div>

          {/* Comparar um vendedor com outro é do dono (28/09/2026). */}
          {verTodas && (
            <div>
              <label className="rotulo" htmlFor="vendedor">Vendedor</label>
              <select id="vendedor" className="campo" value={vendedorId}
                      onChange={(e) => setVendedorId(e.target.value)}>
                <option value="">Todos</option>
                {(equipe.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.nome}</option>
                ))}
              </select>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-mute">
            <input type="checkbox" className="h-4 w-4" checked={incluirCanceladas}
                   onChange={(e) => setIncluirCanceladas(e.target.checked)} />
            Mostrar vendas canceladas
          </label>

          <CampoBusca
            valor={busca}
            aoMudar={setBusca}
            placeholder="Buscar por código, cliente, vendedor ou observação"
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              {/* Botões, não <select multiple>: no celular o select múltiplo é
                  uma lista cega, e aqui o polegar liga e desliga o que quer. */}
              <p className="rotulo">Forma de pagamento</p>
              <div className="flex flex-wrap gap-1.5">
                {FORMAS_DE_PAGAMENTO.map((f) => {
                  const ligada = formas.includes(f);
                  return (
                    <button key={f} type="button" aria-pressed={ligada}
                            onClick={() => setFormas(ligada
                              ? formas.filter((x) => x !== f) : [...formas, f])}
                            className={`border px-2.5 py-1 text-xs font-bold ${
                              ligada ? 'border-signal bg-signal/10' : 'border-edge text-mute'}`}>
                      {rotuloDaForma(f)}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-dim">
                {formas.length === 0
                  ? 'Todas — toque para escolher uma ou mais.'
                  : 'Venda com pagamento dividido entra se qualquer parte casar.'}
              </p>
            </div>
            <div>
              <label className="rotulo" htmlFor="canal">Canal</label>
              <select id="canal" className="campo py-2" value={canal}
                      onChange={(e) => setCanal(e.target.value as SaleChannel | 'todos')}>
                <option value="todos">Todos</option>
                {CANAIS_DE_VENDA.map((c) => (
                  <option key={c} value={c}>{rotuloDoCanalDeVenda(c)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Ordenacao opcoes={ORDENS_VENDA} valor={ordem} aoMudar={setOrdem} />
            {filtrando && (
              <button type="button" onClick={limparFiltros}
                      className="border border-edge px-3 py-1 text-xs font-bold text-dim">
                Limpar filtros
              </button>
            )}
          </div>
        </section>

        {/* A PORTA DIRETA PARA UMA VENDA, pelo código do papel. Desde
            07/10/2026 o gerente TEM a lista de todos, então ela deixou de ser
            o único caminho até a venda do colega — e continua valendo por
            outro motivo: acha a venda de QUALQUER data sem mexer no período,
            que é o gesto de quem está com a notinha do cliente na mão. O dono
            não precisa dela: a lista dele já tem tudo e ele troca o período à
            vontade. */}
        {gerencia && !verFaturamento && (
          <BuscaPeloCodigo podeCorrigir={podeCorrigirVenda(papel)} />
        )}

        {/* O TOPO DO PERÍODO, em duas metades que o dono separou (07/10/2026).
            DINHEIRO — "Vendido" e "Ticket médio" — é só do dono: são,
            exatamente, "o que faturamos hoje". QUANTIDADE fica com os dois: o
            dono autorizou a contagem para o gerente ("só o número de vendas,
            nunca dinheiro"), e ela é o que ele usa para documentar o dia.
            E `totaisDeVendas` não CALCULA o dinheiro quando quem olha não pode
            vê-lo: a soma nem chega a existir na memória do aparelho dele. */}
        {(verFaturamento || verTodas) && (
          <section className={`grid gap-2 sm:gap-3 ${verFaturamento ? 'grid-cols-3' : 'grid-cols-1'}`}>
            {verFaturamento && (
              <div className="cartao p-3 sm:p-4">
                <p className="rotulo">Vendido</p>
                <p className="text-base font-black whitespace-nowrap tabular-nums sm:text-2xl">
                  {brl(totais.total ?? 0)}
                </p>
              </div>
            )}
            <div className="cartao p-3 sm:p-4">
              <p className="rotulo">Vendas</p>
              <p className="text-base font-black tabular-nums sm:text-2xl">{totais.quantidade}</p>
            </div>
            {verFaturamento && (
              <div className="cartao p-3 sm:p-4">
                <p className="rotulo">Ticket médio</p>
                <p className="text-base font-black whitespace-nowrap tabular-nums sm:text-2xl">
                  {brl(totais.ticket ?? 0)}
                </p>
              </div>
            )}
          </section>
        )}

        {/* PASSAR AS VENDAS PARA OUTRA PESSOA (01/10/2026) — só o dono, e só
            com um vendedor escolhido no filtro: o que passa é o que está na
            lista, "as vendas do Alex de hoje", nunca a loja inteira. */}
        {podeTrocarVendedorDaVenda(papel) && vendedorId && linhas.length > 0 && (
          <PassarVendas
            key={vendedorId}
            de={(equipe.data ?? []).find((p) => p.id === vendedorId) ?? null}
            vendas={linhas}
            equipe={equipe.data ?? []}
            aoPassar={(n, nome) => {
              setRecadoDaTroca(n === 1
                ? `1 venda passou para ${nome}.`
                : `${n} vendas passaram para ${nome}.`);
              cliente.invalidateQueries({ queryKey: ['vendas'] });
              // A meta é de quem vendeu: a tela de metas relê.
              cliente.invalidateQueries({ queryKey: ['metas'] });
            }}
          />
        )}
        {recadoDaTroca && <Aviso tipo="ok">{recadoDaTroca}</Aviso>}

        {/* EMITIR O FIADO (14/10/2026): com o filtro Fiado ligado, a lista é
            "quem me deve" — e daqui sai o extrato da pessoa, para imprimir na
            loja e assinar com ela, ou em PDF para mandar à oficina. */}
        {formas.includes('fiado') && (emitindoFiado ? (
          <EmitirFiado
            vendas={linhas}
            gerencia={gerencia}
            emitidoPor={perfil?.nome ?? null}
            periodo={{ de, ate }}
            aoAmpliarPeriodo={de > INICIO_DO_SISTEMA
              ? () => { setDe(INICIO_DO_SISTEMA); setAte(hojeISO()); }
              : undefined}
            aoFechar={() => setEmitindoFiado(false)}
          />
        ) : (
          <button type="button" className="botao-primario w-full py-3"
                  onClick={() => setEmitindoFiado(true)}>
            Emitir — imprimir ou salvar PDF do fiado
          </button>
        ))}

        {vendas.error ? <Aviso tipo="erro">{mensagemDeErro(vendas.error)}</Aviso> : null}
        {vendas.isLoading && <Carregando texto="Carregando vendas" />}

        <section className="cartao p-4">
          <div className="flex items-center justify-between">
            <h2 className="rotulo">
              {/* "12 de 87" e não só "12": sem o total, quem filtra não percebe
                  que está olhando um recorte e conclui que o dia foi fraco. */}
              Lançamentos ({filtrando
                ? `${linhas.length} de ${vendas.data?.length ?? 0}`
                : linhas.length})
              {totais.canceladas > 0 && (
                <span className="ml-2 text-erro">· {totais.canceladas} cancelada(s)</span>
              )}
            </h2>
            {/* EXPORTAR É SÓ DO DONO desde 07/10/2026, por decisão dele no
                mesmo dia em que o gerente ganhou a lista inteira: o arquivo
                tem uma linha por venda com o valor, e somar no Excel é um
                clique. É o caminho mais curto até o número que ele não quer
                entregar — e, sendo honesto, o único que o sistema consegue
                fechar. (O vendedor perdeu o CSV das vendas dele junto; é
                efeito da mesma decisão, e volta trocando esta linha por
                `verTodas` se o dono quiser.) */}
            {verFaturamento && (
              <button type="button" onClick={exportar} disabled={!linhas.length}
                      className="botao-terciario">
                Exportar CSV
              </button>
            )}
          </div>

          {linhas.length > 0 ? (
            <ul className="mt-2 divide-y divide-edge">
              {linhas.map((v) => (
                <li key={v.id}>
                  <LinhaVenda
                    venda={v}
                    gerencia={gerencia}
                    podeCorrigir={podeCorrigirVenda(papel)}
                    aberta={aberta === v.id}
                    aoAbrir={() => setAberta(aberta === v.id ? null : v.id)}
                    // UMA venda, pela box ao lado do valor (06/10/2026). O
                    // caminho largo (passar a lista inteira) continua acima,
                    // atrás do filtro por vendedor; este é o caso comum.
                    podeTrocarVendedor={podeTrocarVendedorDaVenda(papel)}
                    aoTrocarVendedor={(nome) => {
                      setRecadoDaTroca(`A venda ${v.codigo} passou para ${nome}.`);
                      cliente.invalidateQueries({ queryKey: ['vendas'] });
                      // A meta é de quem vendeu: a tela de metas relê.
                      cliente.invalidateQueries({ queryKey: ['metas'] });
                    }}
                    aoCancelar={() => {
                      cliente.invalidateQueries({ queryKey: ['vendas'] });
                      cliente.invalidateQueries({ queryKey: ['caixa'] });
                      cliente.invalidateQueries({ queryKey: ['buscar'] });
                      // Cancelar e DEVOLVER mexem na dívida (a devolução abate
                      // o fiado desde a 065) — a tela do fiado e o carimbo do
                      // recibo têm que reler, senão cobram o que já saiu.
                      cliente.invalidateQueries({ queryKey: ['fiados'] });
                      cliente.invalidateQueries({ queryKey: ['quitacao'] });
                      cliente.invalidateQueries({ queryKey: ['devolucoes-venda'] });
                    }}
                  />
                </li>
              ))}
            </ul>
          ) : (
            !vendas.isLoading && (
              // Duas frases diferentes de propósito: "não vendeu nada" e "o
              // filtro escondeu tudo" pedem reações opostas de quem está lendo.
              <p className="mt-3 text-sm text-dim">
                {filtrando && (vendas.data?.length ?? 0) > 0
                  ? 'Nenhuma venda do período casa com esses filtros. Use “Limpar filtros” para ver todas.'
                  : 'Nenhuma venda no período.'}
              </p>
            )
          )}
        </section>
      </div>
    </Layout>
  );
}

// --- Passar as vendas da lista para outra pessoa (só o dono) -----------------
//
// Pedido do dono (01/10/2026): "todas as vendas do Alex de hoje gostaria de
// passar pro nome do Renan, quero que eu consiga fazer isso na minha conta".
// O dono filtra por vendedor e período, confere a lista e passa: vai para o
// banco exatamente o que está na tela (o filtro de busca, forma e canal
// também recortam). Desfazer é o mesmo caminho, de volta.
export function PassarVendas({ de, vendas, equipe, aoPassar }: {
  de: Profile | null;
  vendas: VendaDetalhada[];
  equipe: Profile[];
  aoPassar: (quantas: number, para: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [para, setPara] = useState('');
  const [motivo, setMotivo] = useState('');
  // Só quem está ativo: o desligado não assina venda (o banco também recusa).
  const destinos = equipe.filter((p) => p.ativo && p.id !== de?.id);
  const nomeDoPara = destinos.find((p) => p.id === para)?.nome ?? '';
  const n = vendas.length;
  const passar = useMutation({
    mutationFn: () => trocarVendedorDasVendas({
      vendas: vendas.map((v) => v.id), vendedorId: para, motivo,
    }),
    onSuccess: (quantas) => {
      aoPassar(quantas, nomeDoPara);
      setAberto(false);
      setPara('');
      setMotivo('');
    },
  });

  if (!aberto) {
    return (
      <button type="button" className="botao-terciario" onClick={() => setAberto(true)}>
        Passar {n === 1 ? 'esta venda' : `estas ${n} vendas`}{de ? ` de ${de.nome}` : ''} para outra pessoa
      </button>
    );
  }

  return (
    <section className="cartao space-y-3 p-4" aria-label="Passar vendas para outra pessoa">
      <h2 className="rotulo">
        Passar {n === 1 ? '1 venda' : `${n} vendas`}{de ? ` de ${de.nome}` : ''}
      </h2>
      <p className="text-xs text-dim">
        Passa as vendas que estão na lista abaixo. Muda de quem é a venda — e com
        ela a meta e a base da comissão. Peças, valores e pagamento ficam como estão.
      </p>
      <div>
        <label className="rotulo" htmlFor="passar-para">Para</label>
        <select id="passar-para" className="campo" value={para}
                onChange={(e) => setPara(e.target.value)}>
          <option value="">Escolha a pessoa</option>
          {destinos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
        </select>
      </div>
      <div>
        <label className="rotulo" htmlFor="passar-motivo">Motivo (opcional)</label>
        <input id="passar-motivo" className="campo" value={motivo} maxLength={200}
               placeholder="Ex.: vendeu no login do colega"
               onChange={(e) => setMotivo(e.target.value)} />
      </div>
      {passar.error ? <Aviso tipo="erro">{mensagemDeErro(passar.error)}</Aviso> : null}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="botao-primario" disabled={!para || passar.isPending}
                onClick={() => passar.mutate()}>
          {passar.isPending
            ? 'Passando…'
            : para
              ? `Passar ${n === 1 ? '1 venda' : `${n} vendas`} para ${nomeDoPara}`
              : 'Passar'}
        </button>
        <button type="button" className="botao-terciario" disabled={passar.isPending}
                onClick={() => setAberto(false)}>
          Desistir
        </button>
      </div>
    </section>
  );
}

// --- A venda de OUTRA PESSOA, pelo código do papel ---------------------------
//
// NASCEU EM 28/09/2026, no mesmo dia em que a policy de `sales` deixou de dar
// ao gerente as vendas dos colegas. A decisão do dono tirou dele a VISTA do
// que os outros vendem — não o trabalho: ele continua cancelando, corrigindo,
// devolvendo peça e cobrando fiado de venda que não é dele. Sem esta caixa, a
// lista da tela não tem a venda do colega e o único caminho até ela seria
// chamar o dono no meio do atendimento. O manual já prometia o gesto ("pelo
// código do papel"); era a função que faltava.
//
// E note o que ela NÃO é: não lista, não soma, não filtra. Uma venda por vez,
// e só para quem tem o código na mão — o cliente traz o recibo. É essa
// estreiteza que a mantém do lado certo da decisão do dono.
export function BuscaPeloCodigo({ podeCorrigir }: { podeCorrigir: boolean }) {
  const cliente = useQueryClient();
  // VIR DE FORA APONTANDO PARA A VENDA (?codigo=VD-000123) — o mesmo gesto do
  // ?venda=<id> lá em cima, e pelo mesmo motivo: quem chega com a venda na mão
  // não deve ter de digitá-la de novo. A diferença é que o ?venda= depende de
  // a venda estar na LISTA, e para o gerente a venda do colega não está.
  const [params] = useSearchParams();
  const codigoAlvo = params.get('codigo') ?? '';
  const [texto, setTexto] = useState(codigoAlvo);
  // O código PEDIDO, separado do que está sendo digitado: sem isso a consulta
  // sairia a cada tecla, e 'VD-00' é uma pergunta que ninguém quis fazer.
  const [codigo, setCodigo] = useState(codigoAlvo);
  const [aberta, setAberta] = useState(true);

  const venda = useQuery({
    queryKey: ['venda-por-codigo', codigo],
    queryFn: () => vendaPeloCodigo(codigo),
    enabled: codigo !== '',
  });

  const procurar = () => {
    const limpo = texto.trim();
    if (limpo === '') return;
    // A normalização ('123' → 'VD-000123') é do BANCO, como todo código da
    // casa: duas normalizações em dois lugares divergem na primeira mudança de
    // formato. Aqui só se recusa o campo vazio.
    setCodigo(limpo);
    setAberta(true);
  };

  return (
    <section className="cartao space-y-2 p-4">
      <h2 className="rotulo">Abrir a venda de outra pessoa</h2>
      <p className="text-xs text-dim">
        Pelo código do recibo que o cliente trouxe — <span className="font-mono">VD-000123</span>{' '}
        ou só <span className="font-mono">123</span>. É por aqui que se cancela,
        corrige, devolve peça ou reimprime uma venda que não é sua.
      </p>
      <div className="flex gap-2">
        <input
          id="venda-codigo"
          className="campo py-2 font-mono"
          value={texto}
          placeholder="VD-000123"
          aria-label="Código da venda"
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') procurar(); }}
        />
        <button type="button" className="botao-secundario px-4 py-2 text-sm disabled:opacity-50"
                disabled={texto.trim() === ''} onClick={procurar}>
          Abrir
        </button>
      </div>

      {/* `isPending`, e não `isFetching`: com a venda já na tela, o refetch em
          segundo plano não precisa cobri-la com um "procurando". */}
      {codigo !== '' && venda.isPending && <Carregando texto="Procurando a venda" />}
      {venda.error ? <Aviso tipo="erro">{mensagemDeErro(venda.error)}</Aviso> : null}
      {/* `null` não é erro: é código digitado errado, e o balcão erra um
          dígito com frequência. A mensagem diz o que fazer, não o que houve —
          e é AVISO (role=status), não faixa vermelha de defeito. */}
      {venda.isSuccess && !venda.data && (
        <Aviso tipo="alerta">
          Nenhuma venda com esse código. Confira os números no recibo do cliente.
        </Aviso>
      )}

      {venda.data && (
        <div className="border-t border-edge pt-2">
          {/* SEM a box "Mudar vendedor": esta caixa é a do GERENTE (o dono tem
              a lista inteira), e trocar quem vendeu é só do dono. Não passar os
              props é o que a esconde. */}
          <LinhaVenda
            venda={venda.data}
            gerencia
            podeCorrigir={podeCorrigir}
            aberta={aberta}
            aoAbrir={() => setAberta(!aberta)}
            aoCancelar={() => {
              // A MESMA invalidação da lista, mais esta busca: cancelar aqui
              // muda a venda, o caixa, a dívida e o carimbo do recibo.
              cliente.invalidateQueries({ queryKey: ['venda-por-codigo'] });
              cliente.invalidateQueries({ queryKey: ['vendas'] });
              cliente.invalidateQueries({ queryKey: ['caixa'] });
              cliente.invalidateQueries({ queryKey: ['buscar'] });
              cliente.invalidateQueries({ queryKey: ['fiados'] });
              cliente.invalidateQueries({ queryKey: ['quitacao'] });
              cliente.invalidateQueries({ queryKey: ['devolucoes-venda'] });
            }}
          />
        </div>
      )}
    </section>
  );
}

/**
 * Os números do topo — e o dinheiro SÓ é calculado para quem pode vê-lo
 * (07/10/2026).
 *
 * Pura e exportada porque é aqui que mora a metade do pedido do dono que não
 * se vê na tela: com `podeSomar` falso, `total` e `ticket` voltam NULOS, então
 * a soma das vendas do gerente não existe nem na memória do aparelho dele —
 * não há número para um cartão novo, um log ou um refator distraído publicar
 * depois. A CONTAGEM fica para os dois: o dono autorizou ("só o número de
 * vendas, nunca dinheiro"), e as canceladas também, que são operação.
 */
export function totaisDeVendas(linhas: VendaDetalhada[], podeSomar: boolean): {
  total: number | null; quantidade: number; ticket: number | null; canceladas: number;
} {
  const validas = linhas.filter((v) => !v.cancelada_em);
  const total = podeSomar ? validas.reduce((s, v) => s + Number(v.total), 0) : null;
  return {
    total,
    quantidade: validas.length,
    ticket: total === null ? null : (validas.length ? total / validas.length : 0),
    canceladas: linhas.filter((v) => v.cancelada_em).length,
  };
}

/** Exportada para o teste: é a linha que PRECISA continuar mostrando o valor
 *  da venda a quem a fez (28/09/2026). */
export function LinhaVenda({
  venda, gerencia, podeCorrigir, aberta, aoAbrir, aoCancelar,
  podeTrocarVendedor, aoTrocarVendedor,
}: {
  venda: VendaDetalhada;
  gerencia: boolean;
  /** 070: so o dono reescreve pecas e valores. Vem pronto de cima, como
   *  `gerencia` — o cartao nao decide permissao, so obedece. */
  podeCorrigir: boolean;
  aberta: boolean;
  aoAbrir: () => void;
  aoCancelar: () => void;
  /** A box "Mudar vendedor" ao lado do valor (06/10/2026) — só o dono. Os dois
   *  são OPCIONAIS e a box só aparece com os dois: quem não passa nada fica
   *  sem o botão, que é o lado certo para errar. */
  podeTrocarVendedor?: boolean;
  aoTrocarVendedor?: (nome: string) => void;
}) {
  const [cancelando, setCancelando] = useState(false);
  // null = fechado; 'itens' = escolher peça a peça; 'tudo' = já vem com a
  // venda inteira marcada (o atalho "virar a ver").
  const [devolvendo, setDevolvendo] = useState<null | 'itens' | 'tudo'>(null);
  const [editando, setEditando] = useState(false);
  const [corrigindo, setCorrigindo] = useState(false);
  const [trocandoVendedor, setTrocandoVendedor] = useState(false);
  // Qual item está aberto na sanfona. Um por vez: o cartão da venda já é
  // comprido, e abrir cinco de uma vez transforma a lista em rolagem.
  const [itemAberto, setItemAberto] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const cancelada = Boolean(venda.cancelada_em);

  // As peças: pelo caminho de quem está olhando (28/09/2026). O vendedor lê
  // `sale_items` direto (a policy lhe dá as linhas das vendas dele); a
  // gerência lê pela função definer, porque a partir de hoje a venda que ela
  // abre pode ser de outra pessoa — e aí a tabela devolveria ZERO peça sem
  // reclamar, deixando o painel de devolução vazio e o recibo sem itens.
  const itens = useQuery({
    queryKey: ['itens-venda', venda.id],
    queryFn: () => lerItensDaVenda(gerencia)(venda.id),
    enabled: aberta,
  });

  // A FOTO de cada peça, sem o fundo branco (01/10/2026) — só quando a venda
  // abre, num lote só. product_images tem leitura pública: vale para os três papéis.
  const idsComFoto = [...new Set((itens.data ?? [])
    .filter((i) => !ehItemAvulso(i) && i.products).map((i) => i.product_id))];
  const fotosDosItens = useQuery({
    queryKey: ['fotos-dos-itens', venda.id, idsComFoto],
    queryFn: () => fotosDasPecas(idsComFoto),
    enabled: aberta && idsComFoto.length > 0,
    staleTime: 60_000,
  });

  // As devoluções desta venda (033) — gerência; a RLS devolve vazio para o
  // vendedor, então nem se pergunta.
  // O acerto do fiado, SÓ quando a venda está aberta e é fiada — o papel
  // precisa saber se já foi pago (25/08/2026). Nunca na lista de 500.
  const quitacao = useQuery({
    queryKey: ['quitacao', venda.id],
    queryFn: () => quitacaoDaVenda(venda.id),
    enabled: aberta && venda.forma_pagamento === 'fiado',
  });

  const devolucoes = useQuery({
    queryKey: ['devolucoes-venda', venda.id],
    queryFn: () => devolucoesDaVenda(venda.id),
    enabled: aberta && gerencia,
  });
  const devs = devolucoes.data ?? [];
  const totalDevolvido = devs.reduce((soma, d) => soma + Number(d.valor), 0);
  // Quanto de cada ITEM já voltou — é o teto do seletor de devolução.
  const jaDevolvido: Record<string, number> = {};
  for (const d of devs) {
    for (const di of d.itens) {
      jaDevolvido[di.sale_item_id] = (jaDevolvido[di.sale_item_id] ?? 0) + Number(di.quantidade);
    }
  }

  const cancelar = useMutation({
    mutationFn: () => cancelarVenda(venda.id, motivo),
    onSuccess: () => { setCancelando(false); setMotivo(''); aoCancelar(); },
  });

  // --- O RECIBO E A NOTINHA DA ENTREGA (061) ---------------------------------
  // Os itens só são buscados quando a sanfona abre (a mesma consulta que a tela
  // já fazia) — imprimir exige tê-los, então o botão espera por eles.
  // A montagem é uma FUNÇÃO PURA em lib/reciboDoc (08/09/2026). Ela era um
  // objeto literal aqui e esquecia `entrega_taxa`: o papel saía sem a linha da
  // entrega e com o total das peças, menor do que o cliente tinha pago.
  const paraImprimir = (): VendaDoRecibo => vendaParaPapel(
    venda,
    (itens.data ?? []).map((i) => ({
      // O id viaja para o recibo casar com as peças já pagas do fiado (065).
      id: i.id,
      // A PEÇA DIVERSA sai pelo nome que o vendedor declarou: no papel do
      // cliente, "PEÇA DIVERSA — BALCÃO" não diz o que ele levou.
      descricao: ehItemAvulso(i) ? (i.descricao_avulsa as string) : i.products?.descricao ?? 'peça',
      quantidade: Number(i.quantidade),
      preco_unit: Number(i.preco_unit),
      subtotal: Number(i.subtotal),
      // 073: só vem preenchido quando a linha é de OUTRO carro.
      veiculo_montadora: i.veiculo_montadora,
      veiculo_modelo: i.veiculo_modelo,
    })),
    quitacao.data ?? null,
  );

  const [papelErro, setPapelErro] = useState<string | null>(null);
  const [papelOk, setPapelOk] = useState<string | null>(null);
  const [combinandoEntrega, setCombinandoEntrega] = useState(false);

  const imprimir = useMutation({
    mutationFn: async (o: { tipo: 'recibo' | 'entrega'; naLoja: boolean }) => {
      const dados = paraImprimir();
      if (dados.itens.length === 0) throw new Error('Abra a venda para carregar as peças.');
      const html = o.tipo === 'entrega'
        ? montarNotinhaDeEntrega(dados) : montarRecibo(dados);
      if (!o.naLoja) { imprimirAqui(html); return 'aqui'; }
      await enfileirarImpressao({
        tipo: 'recibo',
        titulo: `${o.tipo === 'entrega' ? 'Entrega' : 'Recibo'} ${venda.codigo}`
          + (venda.cliente ? ` — ${venda.cliente}` : ''),
        documentoHtml: html,
        refTable: 'sales',
        refId: venda.id,
      });
      return 'loja';
    },
    onSuccess: (onde) => {
      setPapelErro(null);
      setPapelOk(onde === 'loja' ? 'Mandado para a impressora da loja.' : null);
    },
    onError: (e) => { setPapelOk(null); setPapelErro(mensagemDeErro(e)); },
  });

  const entregaFeita = useMutation({
    mutationFn: () => marcarEntregaFeita(venda.id),
    onSuccess: aoCancelar,
  });

  const trocaDeVendedor = Boolean(podeTrocarVendedor && aoTrocarVendedor);

  return (
    <div className="py-3">
      {/* A SANFONA E A BOX SÃO IRMÃS, não aninhadas: <button> dentro de
          <button> é HTML inválido, e neste arquivo isso já custou três
          correções (ver a nota do link da ficha, mais abaixo). */}
      <div className="flex items-start justify-between gap-2">
        <button type="button" onClick={aoAbrir} className="flex min-w-0 flex-1 items-start justify-between gap-3 text-left">
          <div className="min-w-0">
            <p className={`font-semibold ${cancelada ? 'text-dim line-through' : ''}`}>
              {venda.vendedor ?? 'sem vendedor'}
              {venda.cliente && <span className="font-normal text-mute"> · {venda.cliente}</span>}
            </p>
            <p className="text-xs text-dim">
              {dataHoraBR(venda.vendida_em)}
              {venda.forma_pagamento && ` · ${descreverPagamento(venda.forma_pagamento, venda.sale_payments)}`}
              {/* O "verificado" (055): esta venda passou pelo caixa. A ausência
                  não é pendência — conferir é opcional, por decisão do dono. */}
              {venda.conferida_em && (
                <span className="ml-1.5 text-ok" title={
                  `Conferida no caixa${venda.conferida_por_nome ? ` por ${venda.conferida_por_nome}` : ''}`
                }>
                  ✓ conferida
                </span>
              )}
            </p>
            {cancelada && (
              <p className="mt-1 text-xs text-erro">Cancelada: {venda.motivo_cancelamento}</p>
            )}
            {/* 070: venda corrigida tem que PARECER corrigida. Sem esta linha, o
                conteudo mudaria em silencio e a correcao ficaria indistinguivel
                de adulteracao para quem lesse a lista depois. */}
            {!cancelada && venda.versao > 1 && (
              <p className="mt-1 text-xs text-alerta-claro">
                <span className="ponto mr-1.5 align-middle text-alerta" aria-hidden="true" />
                Corrigida{venda.corrigida_em ? ` em ${dataHoraBR(venda.corrigida_em)}` : ''}
                {venda.motivo_correcao && <span className="text-mute">: {venda.motivo_correcao}</span>}
              </p>
            )}
          </div>
          <div className="shrink-0 text-right">
            <p className={`font-bold tabular-nums ${cancelada ? 'text-dim line-through' : 'text-paper'}`}>
              {brl(Number(venda.total))}
            </p>
            <span className="text-xs text-dim">{aberta ? '▲' : '▼'}</span>
          </div>
        </button>
        {/* MUDAR VENDEDOR, AO LADO DO VALOR (06/10/2026, pedido do dono). Duas
            linhas de propósito: a 375 px, "Mudar vendedor" numa linha só come o
            espaço do nome e da hora, e a hora é o que o dono usa para reconhecer
            a venda. */}
        {trocaDeVendedor && (
          <button type="button"
                  className="shrink-0 border border-edge px-1.5 py-1 text-center text-[0.625rem] font-bold uppercase leading-tight tracking-wide text-mute transition-colors hover:border-signal hover:text-paper"
                  title="Mudar o vendedor desta venda"
                  aria-label="Mudar vendedor desta venda"
                  onClick={() => setTrocandoVendedor(true)}>
            <span className="block">Mudar</span>
            <span className="block">vendedor</span>
          </button>
        )}
      </div>

      {trocaDeVendedor && trocandoVendedor && (
        <MudarVendedorDaVenda
          venda={venda}
          aoFechar={() => setTrocandoVendedor(false)}
          aoTrocar={(nome) => { setTrocandoVendedor(false); aoTrocarVendedor?.(nome); }}
        />
      )}

      {aberta && (
        <div className="mt-3 border-l-2 border-edge pl-3">
          {itens.isLoading && <p className="text-xs text-dim">Carregando itens…</p>}
          {itens.data && itens.data.length > 0 && (
            <ul className="space-y-1">
              {itens.data.map((i) => {
                const aberto = itemAberto === i.id;
                const avulso = ehItemAvulso(i);
                const fotosDoItem = avulso ? [] : (fotosDosItens.data ?? []).filter((f) => f.product_id === i.product_id);
                return (
                  <li key={i.id}>
                    {/* A linha inteira é o botão da sanfona. O link para a
                        ficha fica FORA dele: <a> dentro de <button> é HTML
                        inválido, e neste projeto isso já custou três correções.
                        A foto também: é botão IRMÃO, à esquerda (01/10/2026). */}
                    <div className="flex items-center gap-2">
                    <MiniaturaDaPeca productId={avulso ? null : i.product_id} fotos={fotosDoItem}
                                     descricao={nomeDoItemVendido(i)} tamanho={40} />
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-baseline justify-between gap-3 py-0.5 text-left text-xs hover:text-paper"
                      aria-expanded={aberto}
                      onClick={() => setItemAberto(aberto ? null : i.id)}
                    >
                      <span className="min-w-0">
                        <span className="text-dim">{aberto ? '▾' : '▸'} </span>
                        <span className="text-mute">{qtd(i.quantidade)}× </span>
                        {nomeDoItemVendido(i)}
                        {avulso && (
                          <span className="ml-1 text-[0.625rem] uppercase text-alerta">diversa</span>
                        )}
                        {/* O CÓDIGO aparece AQUI, e não no recibo — e é o código
                            pela régua da casa (codigoDaPeca): o digitado na peça
                            diversa (071), senão o de FÁBRICA, senão o OEM, e só
                            por último o sku da loja. Até 11/09/2026 a peça
                            cadastrada mostrava só o sku, que serve para bipar a
                            etiqueta e não para pedir ao fornecedor — Orçamento e
                            Encomendas já mostravam o certo; a venda ficou para
                            trás, e é na venda que o dono e o Diego conferem. */}
                        {(() => {
                          const codigo = codigoDaPeca({
                            codigo_avulso: i.codigo_avulso,
                            sku: i.products?.sku,
                            produto: i.products,
                          });
                          return codigo
                            ? <span className="ml-1 font-mono text-dim">{codigo}</span>
                            : null;
                        })()}
                      </span>
                      <span className="shrink-0 font-mono tabular-nums">{brl(Number(i.subtotal))}</span>
                    </button>
                    </div>

                    {aberto && (
                      <div className="mb-1 ml-3 border-l border-edge pl-2 text-xs">
                        {avulso ? (
                          // Peça diversa não tem cadastro para consultar — o que
                          // existe é o que o vendedor escreveu na hora. Mandar
                          // para a ficha do coringa 9999 não ajudaria ninguém.
                          <p className="text-mute">
                            Peça diversa, vendida sem cadastro. O que está escrito
                            na linha é tudo o que o sistema sabe dela.
                          </p>
                        ) : (
                          <>
                            {i.products?.observacoes ? (
                              <p className="whitespace-pre-line text-mute">
                                {i.products.observacoes}
                              </p>
                            ) : (
                              <p className="text-dim">Sem observações no cadastro desta peça.</p>
                            )}
                            <p className="mt-1 text-dim">
                              {i.products?.marca ? `${i.products.marca} · ` : ''}
                              {brl(Number(i.preco_unit))} a unidade
                              {i.products?.codigo_oem ? ` · OEM ${i.products.codigo_oem}` : ''}
                              {i.products?.codigo_fabrica ? ` · fab. ${i.products.codigo_fabrica}` : ''}
                            </p>
                            {i.product_id && (
                              <Link to={`/peca/${i.product_id}`}
                                    className="mt-1 inline-block text-signal-soft underline">
                                abrir a ficha da peça
                              </Link>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {Number(venda.desconto) > 0 && (
            <p className="mt-1 text-xs text-mute">Desconto: {brl(Number(venda.desconto))}</p>
          )}
          {venda.veiculo_modelo && (
            <p className="mt-1 text-xs text-mute">
              <span className="text-dim">Carro:</span>{' '}
              {textoDoCarro(venda.veiculo_montadora, venda.veiculo_modelo, venda.veiculo_ano)}
            </p>
          )}
          {venda.observacao && <p className="mt-1 text-xs text-mute">{venda.observacao}</p>}

          {gerencia && devs.length > 0 && (
            <div className="mt-3 border border-alerta/40 bg-alerta/5 p-3">
              <p className="text-xs font-bold text-alerta">
                Trocas e estornos desta venda · {brl(totalDevolvido)}
              </p>
              <ul className="mt-1 space-y-2">
                {devs.map((d) => {
                  // Desde a 065 a devolução pode ter ido toda (ou em parte)
                  // para a dívida, e aí não existe vale nem estorno — dizer
                  // "Troca (vale )" sem código seria mentir por omissão.
                  const noFiado = Number(d.abatido_no_fiado ?? 0);
                  const soNoFiado = noFiado >= Number(d.valor) - 0.005;
                  return (
                  <li key={d.id} className="text-xs text-mute">
                    <p>
                      <span className="font-semibold">
                        {soNoFiado
                          ? 'Devolução (abatida do fiado)'
                          : d.modo === 'vale'
                            ? `Troca (vale ${d.codigo_vale ?? ''})`
                            : `Estorno (${d.forma_pagamento ?? ''})`}
                      </span>
                      {' · '}{brl(Number(d.valor))} · {dataHoraBR(d.created_at)}
                      {d.registrado_por_nome ? ` · por ${d.registrado_por_nome}` : ''}
                    </p>
                    {noFiado > 0 && !soNoFiado && (
                      <p className="text-dim">
                        {brl(noFiado)} abatidos do fiado; o resto saiu como acima.
                      </p>
                    )}
                    <p>
                      {d.itens.map((di) => `${qtd(Number(di.quantidade))}× ${di.descricao}`).join(' · ')}
                    </p>
                    <p className="text-dim">{d.motivo}</p>
                  </li>
                  );
                })}
              </ul>
            </div>
          )}

          {gerencia && !cancelada && devolvendo && (
            <PainelDevolucao
              saleId={venda.id}
              itens={itens.data ?? []}
              jaDevolvido={jaDevolvido}
              // "virar a ver" abre com tudo marcado: é o pedido do dono de
              // emitir um vale no valor da venda sem catar peça por peça.
              iniciarComTudo={devolvendo === 'tudo'}
              aoFechar={() => setDevolvendo(null)}
              aoRegistrar={() => { setDevolvendo(null); aoCancelar(); }}
            />
          )}

          {gerencia && !cancelada && editando && (
            <PainelEdicao
              venda={venda}
              aoFechar={() => setEditando(false)}
              aoSalvar={() => { setEditando(false); aoCancelar(); }}
            />
          )}

          {/* O PAPEL DA VENDA (061). Fica FORA do bloco de gerência: quem
              atendeu é quem entrega o recibo ao cliente, e quem combina a
              entrega é quem falou com ele. */}
          {!cancelada && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-dim">Papel:</span>
              <button type="button" className="border border-edge px-3 py-2 text-xs font-bold hover:border-signal"
                      disabled={imprimir.isPending}
                      onClick={() => imprimir.mutate({ tipo: 'recibo', naLoja: true })}>
                Recibo na loja
              </button>
              <button type="button" className="border border-edge px-3 py-2 text-xs text-mute hover:border-signal"
                      onClick={() => imprimir.mutate({ tipo: 'recibo', naLoja: false })}>
                Recibo aqui
              </button>
              {venda.entrega && (
                <button type="button" className="border border-signal px-3 py-2 text-xs font-bold text-signal-soft"
                        disabled={imprimir.isPending}
                        onClick={() => imprimir.mutate({ tipo: 'entrega', naLoja: true })}>
                  Notinha da entrega
                </button>
              )}
              <button type="button" className="border border-edge px-3 py-2 text-xs font-bold hover:border-signal"
                      onClick={() => setCombinandoEntrega((v) => !v)}>
                {venda.entrega ? 'Mudar entrega' : 'É entrega'}
              </button>
              {venda.entrega && !venda.entregue_em && (
                <button type="button" className="border border-ok px-3 py-2 text-xs font-bold text-ok"
                        disabled={entregaFeita.isPending}
                        onClick={() => entregaFeita.mutate()}>
                  Entregue
                </button>
              )}
              {venda.entregue_em && (
                <span className="text-xs text-ok">
                  ✓ entregue{venda.entregue_por_nome ? ` por ${venda.entregue_por_nome}` : ''}
                </span>
              )}
            </div>
          )}

          {papelErro ? <div className="mt-2"><Aviso tipo="alerta">{papelErro}</Aviso></div> : null}
          {papelOk ? <div className="mt-2"><Aviso tipo="ok">{papelOk}</Aviso></div> : null}

          {combinandoEntrega && !cancelada && (
            <PainelEntrega
              venda={venda}
              aoFechar={() => setCombinandoEntrega(false)}
              aoSalvar={() => { setCombinandoEntrega(false); aoCancelar(); }}
            />
          )}

          {corrigindo && !cancelada && (
            <PainelCorrecao
              venda={venda}
              aoFechar={() => setCorrigindo(false)}
              aoSalvar={() => { setCorrigindo(false); aoCancelar(); }}
            />
          )}

          {gerencia && !cancelada && (
            <div className="mt-3 flex flex-wrap gap-2">
              {!devolvendo && !cancelando && !editando && (
                <button type="button" onClick={() => setEditando(true)}
                        className="border border-edge px-3 py-2 text-xs font-bold hover:border-signal">
                  Editar
                </button>
              )}
              {/* 070: reescrever o CONTEUDO e do dono. Fica ao lado de Editar
                  porque e a mesma familia de conserto, e a distancia entre as
                  duas e o que a tela precisa ensinar: uma corrige a digitacao,
                  a outra corrige o dinheiro. */}
              {podeCorrigir && !devolvendo && !cancelando && !editando && !corrigindo && (
                <button type="button" onClick={() => setCorrigindo(true)}
                        className="border border-edge px-3 py-2 text-xs font-bold hover:border-signal">
                  Corrigir pecas e valores
                </button>
              )}
              {!devolvendo && !cancelando && !editando && (
                <button type="button" onClick={() => setDevolvendo('itens')}
                        className="border border-edge px-3 py-2 text-xs font-bold hover:border-signal">
                  Devolver / trocar
                </button>
              )}
              {/* O atalho que o dono pediu (28/08): emitir um "a ver" no valor
                  da venda sem catar peça por peça. Abre o mesmo painel com
                  tudo marcado — a peça VOLTA para o estoque, que é o que
                  torna o crédito honesto. */}
              {!devolvendo && !cancelando && !editando && (
                <button type="button" onClick={() => setDevolvendo('tudo')}
                        className="border border-edge px-3 py-2 text-xs font-bold hover:border-signal">
                  Virar "a ver"
                </button>
              )}
              {!cancelando ? (
                !devolvendo && !editando && (
                <button type="button" onClick={() => setCancelando(true)}
                        className="border border-edge px-3 py-2 text-xs font-bold text-erro hover:border-erro">
                  Cancelar venda
                </button>
                )
              ) : (
                <div className="space-y-2 border border-erro/40 bg-erro/5 p-3">
                  <p className="text-xs text-mute">
                    O cancelamento devolve as peças ao estoque e lança o estorno no caixa.
                    A venda não é apagada — fica registrada como cancelada, com o motivo.
                  </p>
                  <input
                    className="campo py-2 text-sm"
                    value={motivo}
                    autoFocus
                    placeholder="Motivo (obrigatório): peça errada, desistência…"
                    onChange={(e) => setMotivo(e.target.value)}
                  />
                  {cancelar.error ? <Aviso tipo="erro">{mensagemDeErro(cancelar.error)}</Aviso> : null}
                  <div className="flex gap-2">
                    <button type="button" className="botao-secundario flex-1 py-2 text-sm"
                            onClick={() => setCancelando(false)}>
                      Voltar
                    </button>
                    <button type="button"
                            className="botao-perigo flex-1 px-3 py-2 text-sm disabled:opacity-50"
                            disabled={cancelar.isPending || !motivo.trim()}
                            onClick={() => cancelar.mutate()}>
                      {cancelar.isPending ? 'Cancelando…' : 'Confirmar cancelamento'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** O que a correção da venda tem na mão enquanto a pessoa mexe. */
export interface EstadoDaEdicao {
  nomeCliente: string;
  clienteId: string | null;
  observacao: string;
  forma: PaymentMethod;
  carro: CarroEscolhido;
}

/**
 * O estado com que o PainelEdicao NASCE: o que a venda já tem. A RPC grava o
 * que chega — campo que não for enviado vira nulo —, então salvar sem mexer
 * tem de reenviar exatamente o que estava lá.
 *
 * 073 — o carro também se corrige aqui, e nasce SEM o texto digitado
 * (lib/carro.ts, carroDaVenda): o campo mostra o carro remontado e nada é
 * relido até alguém mexer nele — corrigir só a observação de uma venda de
 * setembro reenvia a versão FIPE byte a byte.
 *
 * Função à parte (revisão do PR #271) para o teste provar o MESMO começo que
 * a tela usa, e não uma cópia dele.
 */
export function estadoInicialDaEdicao(venda: VendaDetalhada): EstadoDaEdicao {
  return {
    nomeCliente: venda.cliente ?? '',
    clienteId: venda.customer_id ?? null,
    observacao: venda.observacao ?? '',
    forma: venda.forma_pagamento ?? 'dinheiro',
    carro: carroDaVenda(venda),
  };
}

/** Quando a forma de pagamento NÃO muda por aqui. */
export function travasDaEdicao(venda: Pick<VendaDetalhada, 'forma_pagamento'>): {
  pagaComVale: boolean; dividida: boolean; formaTravada: boolean;
} {
  // Venda paga com vale não troca de forma por aqui: 'a_ver' é lastro
  // (crédito consumido), não digitação — a RPC recusa, e a tela explica antes
  // de a pessoa tentar.
  const pagaComVale = venda.forma_pagamento === 'a_ver';
  // 075: a venda DIVIDIDA também não troca de forma por aqui — as partes já
  // estão no caixa, cada uma pela sua forma. A RPC recusa igual (trava 5).
  const dividida = venda.forma_pagamento === 'misto';
  return { pagaComVale, dividida, formaTravada: pagaComVale || dividida };
}

/** O que o botão "Salvar" manda a editar_venda — este objeto, e nenhum outro:
 *  o mutationFn do PainelEdicao é `editarVenda(pedidoDeEdicao(...))`. */
export function pedidoDeEdicao(
  venda: VendaDetalhada, e: EstadoDaEdicao,
): Parameters<typeof editarVenda>[0] {
  return {
    saleId: venda.id,
    cliente: e.nomeCliente.trim() || null,
    customerId: e.clienteId,
    observacao: e.observacao.trim() || null,
    forma: travasDaEdicao(venda).formaTravada ? null : e.forma,
    ...carroParaRpc(e.carro),
  };
}

/**
 * Corrigir a DIGITAÇÃO de uma venda (gerente/dono, 17/08/2026): o cliente
 * errado, a observação que faltou, a forma que o cliente acabou pagando
 * diferente. Item, quantidade, preço e desconto NÃO estão aqui de propósito —
 * mudar isso é refazer a venda, e para isso existem "Devolver / trocar"
 * (parcial) e "Cancelar venda" (inteira), que devolvem estoque e acertam o
 * caixa. A RPC recusa de novo, do lado do banco, o que esta tela não oferece.
 */
export function PainelEdicao({ venda, aoFechar, aoSalvar }: {
  venda: VendaDetalhada;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [inicial] = useState(() => estadoInicialDaEdicao(venda));
  const [nomeCliente, setNomeCliente] = useState(inicial.nomeCliente);
  const [clienteId, setClienteId] = useState<string | null>(inicial.clienteId);
  const [observacao, setObservacao] = useState(inicial.observacao);
  const [forma, setForma] = useState<PaymentMethod>(inicial.forma);
  const [carro, setCarro] = useState<CarroEscolhido>(inicial.carro);

  const { pagaComVale, formaTravada } = travasDaEdicao(venda);

  const salvar = useMutation({
    mutationFn: () => editarVenda(pedidoDeEdicao(venda, {
      nomeCliente, clienteId, observacao, forma, carro,
    })),
    onSuccess: aoSalvar,
  });

  return (
    <div className="mt-3 space-y-3 border border-signal/40 bg-signal/5 p-3">
      <p className="text-xs text-mute">
        Corrige o que foi digitado. As peças, as quantidades e os valores não
        mudam aqui — para isso, "Devolver / trocar" ou "Cancelar venda".
      </p>

      <SeletorCliente
        clienteId={clienteId}
        clienteNome={nomeCliente}
        aoEscolher={({ id, nome }) => { setClienteId(id); setNomeCliente(nome); }}
      />

      <SeletorVeiculo
        valor={carro}
        aoTrocar={setCarro}
        clienteId={clienteId}
        idPrefixo={`carro-edicao-${venda.id}`}
        rotulo="Carro"
      />

      <div>
        <label className="rotulo" htmlFor={`obs-${venda.id}`}>Observação</label>
        <input
          id={`obs-${venda.id}`}
          className="campo py-2 text-sm"
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
        />
      </div>

      <div>
        <label className="rotulo" htmlFor={`forma-${venda.id}`}>Forma de pagamento</label>
        {formaTravada ? (
          <p className="text-xs text-dim">
            {pagaComVale ? (
              <>Esta venda foi paga com vale "a ver" — a forma não muda por aqui,
              porque representa crédito do cliente que já foi consumido. Para
              mudar, cancele e registre de novo.</>
            ) : (
              <>Pagamento dividido ({descreverPagamento(venda.forma_pagamento, venda.sale_payments)})
              — a forma não muda por aqui: as partes já estão no caixa, cada uma
              pela sua forma. Para mudar a divisão, cancele e registre de novo.</>
            )}
          </p>
        ) : (
          <select id={`forma-${venda.id}`} className="campo" value={forma}
                  onChange={(e) => setForma(e.target.value as PaymentMethod)}>
            <option value="dinheiro">Dinheiro</option>
            <option value="pix">PIX</option>
            <option value="debito">Débito</option>
            <option value="credito">Crédito</option>
            <option value="transferencia">Transferência</option>
            <option value="boleto">Boleto</option>
            <option value="fiado">Fiado</option>
          </select>
        )}
      </div>

      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}

      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                disabled={salvar.isPending} onClick={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando…' : 'Salvar correção'}
        </button>
      </div>
    </div>
  );
}

/**
 * Combinar a entrega: onde, com quem, e — quando a venda saiu fiada — o que o
 * entregador vai cobrar.
 *
 * A forma prevista e o troco só aparecem na venda FIADA. Numa venda já paga,
 * perguntar "como vai pagar?" é o caminho mais curto para o cliente ser
 * cobrado duas vezes na porta de casa.
 */
function PainelEntrega({ venda, aoFechar, aoSalvar }: {
  venda: VendaDetalhada; aoFechar: () => void; aoSalvar: () => void;
}) {
  const [endereco, setEndereco] = useState(venda.entrega_endereco ?? '');
  const [contato, setContato] = useState(venda.entrega_contato ?? '');
  const [observacao, setObservacao] = useState(venda.entrega_observacao ?? '');
  const [forma, setForma] = useState<PaymentMethod | ''>(venda.entrega_forma_prevista ?? '');
  const [troco, setTroco] = useState(
    venda.entrega_troco_para === null ? '' : String(venda.entrega_troco_para).replace('.', ','));
  const aReceber = venda.forma_pagamento === 'fiado';

  const salvar = useMutation({
    mutationFn: () => definirEntrega({
      saleId: venda.id,
      endereco,
      contato,
      observacao,
      formaPrevista: aReceber ? (forma || null) : null,
      trocoPara: aReceber ? paraNumero(troco) : null,
    }),
    onSuccess: aoSalvar,
  });
  const desmarcar = useMutation({
    mutationFn: () => cancelarEntrega(venda.id),
    onSuccess: aoSalvar,
  });

  return (
    <div className="mt-3 space-y-2 border border-signal/40 bg-signal/5 p-3">
      <p className="text-xs text-mute">
        O endereço fica <strong>congelado nesta venda</strong> — cliente que mudar
        de casa não reescreve a entrega de hoje.
      </p>
      <div>
        <label className="rotulo" htmlFor={`end-${venda.id}`}>Endereço *</label>
        <input id={`end-${venda.id}`} className="campo py-2" value={endereco} autoFocus
               placeholder="Rua, número, complemento, bairro"
               onChange={(e) => setEndereco(e.target.value)} />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor={`tel-${venda.id}`}>Telefone</label>
          <input id={`tel-${venda.id}`} className="campo py-2" inputMode="tel" value={contato}
                 placeholder="(53) 9…" onChange={(e) => setContato(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor={`obs-${venda.id}`}>Ponto de referência</label>
          <input id={`obs-${venda.id}`} className="campo py-2" value={observacao}
                 placeholder="Portão azul, deixar na portaria…"
                 onChange={(e) => setObservacao(e.target.value)} />
        </div>
      </div>

      {aReceber && (
        <div className="grid gap-2 border-t border-edge pt-2 sm:grid-cols-2">
          <div>
            <label className="rotulo" htmlFor={`fp-${venda.id}`}>Vai pagar como</label>
            <select id={`fp-${venda.id}`} className="campo py-2" value={forma}
                    onChange={(e) => setForma(e.target.value as PaymentMethod | '')}>
              <option value="">a combinar</option>
              {(['dinheiro', 'pix', 'debito', 'credito'] as const).map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="rotulo" htmlFor={`tr-${venda.id}`}>Levar troco para (R$)</label>
            <input id={`tr-${venda.id}`} className="campo py-2" inputMode="decimal" value={troco}
                   placeholder="ex.: 200,00" onChange={(e) => setTroco(e.target.value)} />
            <p className="mt-1 text-xs text-dim">
              Com quanto o cliente vai pagar — o entregador não sai sem trocado.
            </p>
          </div>
        </div>
      )}

      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
      {desmarcar.error ? <Aviso tipo="erro">{mensagemDeErro(desmarcar.error)}</Aviso> : null}

      <div className="flex flex-wrap gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        {venda.entrega && (
          <button type="button" className="border border-edge px-3 py-2 text-sm text-erro"
                  disabled={desmarcar.isPending} onClick={() => desmarcar.mutate()}>
            Não é entrega
          </button>
        )}
        <button type="button" className="botao-primario flex-1 py-2 text-sm"
                disabled={salvar.isPending || !endereco.trim()}
                onClick={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando…' : 'Salvar entrega'}
        </button>
      </div>
    </div>
  );
}

/**
 * CORRIGIR O CONTEÚDO DA VENDA (070) — só o dono.
 *
 * Pedido dele: "seja valores das peças, desconto, as peças, ou qualquer coisa.
 * Eu, como dono, devo ter permissão de alterar isso". O caso que a pediu foi o
 * desconto aplicado por engano: a peça saiu, o cliente pagou o valor cheio, e
 * o registro ficou errado — devolver desfaria uma entrega que aconteceu, e
 * cancelar daria à venda um código NOVO, deixando o papel do cliente sem par.
 *
 * A TELA MOSTRA A DIFERENÇA ANTES DE CONFIRMAR, e é o ponto todo deste painel.
 * Quem corrige precisa ver, na mesma linha, quanto a venda passa a valer e
 * quanto isso mexe no caixa de hoje — porque é esse número que vai bater (ou
 * não) com a gaveta à noite. Um formulário que só diz "salvo" esconde
 * exatamente o que precisa ser conferido.
 *
 * As peças são as da venda: dá para mudar quantidade e preço, e tirar linha.
 * ADICIONAR peça nova não passa por aqui — precisaria da busca do catálogo
 * inteira dentro deste painel, e o caso real do balcão é corrigir o que foi
 * digitado errado, não montar outra venda. Para essa, o caminho honesto
 * continua sendo cancelar e registrar de novo.
 */
function PainelCorrecao({ venda, aoFechar, aoSalvar }: {
  venda: VendaDetalhada; aoFechar: () => void; aoSalvar: () => void;
}) {
  // Pela função de gerência: corrigir é do dono (podeCorrigirVenda), e o dono
  // corrige a venda de qualquer um — a leitura direta da tabela lhe daria as
  // linhas de todo mundo hoje, mas é a mesma porta do resto da tela e não fica
  // dependendo de uma policy que já estreitou uma vez.
  const { data: itens, isLoading } = useQuery({
    queryKey: ['itens-venda', venda.id],
    queryFn: () => itensDaVendaParaGerencia(venda.id),
  });
  // O histórico só é buscado aqui: é a única tela que o mostra, e a venda
  // nunca corrigida (a esmagadora maioria) não paga por ele.
  const { data: versoes } = useQuery({
    queryKey: ['versoes-venda', venda.id],
    queryFn: () => versoesDaVenda(venda.id),
  });

  // O rascunho nasce do que está gravado. `null` enquanto os itens não
  // chegaram — sem isso, o primeiro render fixaria uma lista vazia e o dono
  // veria a venda sem peça nenhuma (a mesma armadilha do useEffect da folha).
  const [linhas, setLinhas] = useState<Array<{
    product_id: string; descricao: string; quantidade: string; preco: string;
  }> | null>(null);
  const [desconto, setDesconto] = useState(String(venda.desconto ?? 0).replace('.', ','));
  const [motivo, setMotivo] = useState('');
  // A FORMA ENTRA AQUI, e não na edição (pedido do dono, 08/09/2026). A edição
  // corrige digitação e por isso recusa entrar ou sair de 'fiado': lá, trocar a
  // forma deixaria a dívida órfã em contas_receber, ou reescreveria a forma de
  // um dinheiro que já está na gaveta. Nesta tela nada disso acontece — a
  // correção DESFAZ e REFAZ a venda, então a conta a receber morre e nasce com
  // o valor certo, e o caixa recebe só a diferença. Era o caminho que faltava
  // ser oferecido: a RPC já aceitava `p_forma` desde a 070.
  const [forma, setForma] = useState<PaymentMethod>(venda.forma_pagamento ?? 'dinheiro');

  const base = linhas ?? (itens ?? []).map((i) => ({
    product_id: i.product_id,
    descricao: nomeDoItemVendido(i),
    quantidade: String(i.quantidade).replace('.', ','),
    preco: Number(i.preco_unit).toFixed(2).replace('.', ','),
  }));

  const mudar = (idx: number, campo: 'quantidade' | 'preco', valor: string) =>
    setLinhas(base.map((l, i) => (i === idx ? { ...l, [campo]: valor } : l)));
  const tirar = (idx: number) => setLinhas(base.filter((_, i) => i !== idx));

  // A conta, na mesma régua do banco: round por LINHA e depois soma, que é
  // como registrar_venda e corrigir_venda somam. Fazer diferente aqui daria um
  // total de tela que discorda do total gravado por um centavo.
  const bruto = base.reduce((soma, l) => {
    const q = paraNumero(l.quantidade) ?? 0;
    const p = paraNumero(l.preco) ?? 0;
    return soma + Math.round(q * p * 100) / 100;
  }, 0);
  const desc = paraNumero(desconto) ?? 0;
  const novoTotal = Math.max(bruto - desc, 0);
  const novoCobrado = novoTotal + Number(venda.entrega_taxa ?? 0);
  // O que a versão de agora tinha lançado na gaveta. Fiado não lançou nada —
  // o caixa só conhece a dívida no dia do acerto.
  const eraNoCaixa = venda.forma_pagamento === 'fiado' ? 0 : Number(venda.total_cobrado ?? 0);
  // A forma NOVA é quem decide se o dinheiro entra na gaveta — trocar fiado por
  // dinheiro faz entrar o total inteiro hoje, e o contrário faz sair. Ler a
  // forma antiga nas duas pontas mostraria "no caixa: nada muda" numa correção
  // que move o valor cheio.
  const seraNoCaixa = forma === 'fiado' ? 0 : novoCobrado;
  const delta = Math.round((seraNoCaixa - eraNoCaixa) * 100) / 100;
  // 075: na venda DIVIDIDA as peças podem mudar, o valor cobrado não — a
  // diferença teria de sair por UMA das partes, e ninguém sabe qual. A RPC
  // recusa; a tela avisa antes e não deixa confirmar.
  const dividida = venda.forma_pagamento === 'misto';
  const divididaMudaValor = dividida && delta !== 0;

  const corrigir = useMutation({
    mutationFn: async () => {
      if (!motivo.trim()) throw new Error('Escreva o motivo da correção.');
      if (base.length === 0) throw new Error('A venda precisa de ao menos uma peça. Para desfazê-la, cancele.');
      await corrigirVenda({
        saleId: venda.id,
        motivo: motivo.trim(),
        desconto: desc,
        forma,
        itens: base.map((l) => ({
          product_id: l.product_id,
          quantidade: paraNumero(l.quantidade) ?? 0,
          preco_unit: paraNumero(l.preco) ?? 0,
          descricao: l.descricao,
        })),
      });
    },
    onSuccess: aoSalvar,
  });

  if (isLoading) return <div className="mt-3 border border-edge p-3"><Carregando /></div>;

  return (
    <div className="mt-3 space-y-3 border border-signal/40 p-3">
      <div>
        <p className="text-sm font-bold text-signal-soft">Corrigir esta venda</p>
        <p className="mt-1 text-xs text-mute">
          O número e o código <strong>não mudam</strong> — o papel que o cliente levou
          continua valendo. A versão de agora vai para o histórico, e o caixa de
          hoje recebe só a diferença.
        </p>
      </div>

      <ul className="space-y-2">
        {base.map((l, i) => (
          <li key={`${l.product_id}-${i}`} className="border border-edge p-2">
            <p className="text-xs font-bold">{l.descricao}</p>
            <div className="mt-1 grid grid-cols-[1fr_1fr_auto] items-end gap-2">
              <div>
                <label className="rotulo" htmlFor={`q-${venda.id}-${i}`}>Quantidade</label>
                <input id={`q-${venda.id}-${i}`} className="campo py-1.5" inputMode="decimal"
                       value={l.quantidade} onChange={(e) => mudar(i, 'quantidade', e.target.value)} />
              </div>
              <div>
                <label className="rotulo" htmlFor={`p-${venda.id}-${i}`}>Preço da unidade</label>
                <input id={`p-${venda.id}-${i}`} className="campo py-1.5" inputMode="decimal"
                       value={l.preco} onChange={(e) => mudar(i, 'preco', e.target.value)} />
              </div>
              <button type="button" onClick={() => tirar(i)}
                      className="border border-edge px-2 py-1.5 text-xs font-bold text-erro hover:border-erro">
                Tirar
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor={`desc-${venda.id}`}>Desconto (R$)</label>
          <input id={`desc-${venda.id}`} className="campo py-2" inputMode="decimal"
                 value={desconto} onChange={(e) => setDesconto(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor={`forma-corr-${venda.id}`}>Forma de pagamento</label>
          {dividida ? (
            <p id={`forma-corr-${venda.id}`} className="text-xs text-dim">
              {descreverPagamento(venda.forma_pagamento, venda.sale_payments)} — a
              divisão não muda pela correção.
            </p>
          ) : (
          <select id={`forma-corr-${venda.id}`} className="campo py-2" value={forma}
                  onChange={(e) => setForma(e.target.value as PaymentMethod)}>
            <option value="dinheiro">Dinheiro</option>
            <option value="pix">PIX</option>
            <option value="debito">Débito</option>
            <option value="credito">Crédito</option>
            <option value="transferencia">Transferência</option>
            <option value="boleto">Boleto</option>
            <option value="fiado">Fiado</option>
          </select>
          )}
        </div>
      </div>

      <div>
        <label className="rotulo" htmlFor={`mot-${venda.id}`}>Motivo da correção</label>
        <input id={`mot-${venda.id}`} className="campo py-2" value={motivo}
               placeholder="cliente pagou no PIX, não fiado"
               onChange={(e) => setMotivo(e.target.value)} />
      </div>

      {/* O QUE A TROCA DE FORMA FAZ COM A DÍVIDA — dito antes de confirmar,
          porque é a parte que não se vê no total. */}
      {forma !== venda.forma_pagamento && (
        <Aviso tipo="alerta">
          {venda.forma_pagamento === 'fiado' ? (
            <>A dívida deste fiado <strong>deixa de existir</strong> em contas a
            receber, e o valor entra no caixa de hoje. Só funciona enquanto o
            cliente não tiver acertado nada — se já pagou parte, desfaça os
            acertos na tela de Fiado primeiro.</>
          ) : forma === 'fiado' ? (
            <>A venda vira <strong>dívida</strong>: sai do caixa de hoje e passa
            a aparecer em contas a receber, no nome do cliente.</>
          ) : (
            <>Muda só a forma registrada — o valor no caixa é o mesmo.</>
          )}
        </Aviso>
      )}

      {/* O de-para, que é o que se confere antes de confirmar. */}
      <div className="border border-edge p-2 text-xs">
        <p className="flex justify-between">
          <span className="text-dim">era</span>
          <span>{brl(Number(venda.total))} {Number(venda.desconto) > 0 && (
            <span className="text-dim">(desconto {brl(Number(venda.desconto))})</span>
          )}</span>
        </p>
        <p className="mt-0.5 flex justify-between font-bold">
          <span className="text-dim">fica</span>
          <span>{brl(novoTotal)} {desc > 0 && <span className="text-dim">(desconto {brl(desc)})</span>}</span>
        </p>
        {delta !== 0 && (
          <p className="mt-1.5 flex justify-between border-t border-edge pt-1.5">
            <span className="text-dim">no caixa de hoje</span>
            {/* Vermelho só quando SAI dinheiro — a régua de cor do CLAUDE.md:
                vermelho de marca não é vermelho de prejuízo. */}
            <strong className={delta > 0 ? 'text-ok' : 'text-erro'}>
              {delta > 0 ? 'entra ' : 'sai '}{brl(Math.abs(delta))}
            </strong>
          </p>
        )}
        {delta === 0 && (
          <p className="mt-1.5 border-t border-edge pt-1.5 text-dim">
            Não mexe no caixa: o valor cobrado continua o mesmo.
          </p>
        )}
      </div>

      {venda.forma_pagamento === 'fiado' && (
        <Aviso tipo="alerta">
          Venda fiada: a conta do cliente é refeita pelo valor novo. Se ele já
          acertou alguma parcela, o banco recusa — desfaça os acertos primeiro.
        </Aviso>
      )}
      {divididaMudaValor && (
        <Aviso tipo="erro">
          Venda com pagamento dividido: a correção não pode mudar o valor cobrado
          ({brl(eraNoCaixa)}). Corrija mantendo o total, devolva a diferença pela
          tela de devolução, ou cancele e registre de novo.
        </Aviso>
      )}

      {corrigir.error ? <Aviso tipo="erro">{mensagemDeErro(corrigir.error)}</Aviso> : null}

      <div className="flex flex-wrap gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm"
                disabled={corrigir.isPending || !motivo.trim() || divididaMudaValor}
                onClick={() => corrigir.mutate()}>
          {corrigir.isPending ? 'Corrigindo…' : 'Confirmar correção'}
        </button>
      </div>

      {/* O HISTÓRICO. Uma venda corrigida sem histórico visível é
          indistinguível de uma venda adulterada. */}
      {versoes && versoes.length > 0 && (
        <div className="border-t border-edge pt-2">
          <p className="text-xs font-bold text-dim">Correções anteriores</p>
          <ul className="mt-1 space-y-1">
            {versoes.map((v) => (
              <li key={v.versao} className="text-xs">
                <span className="text-dim">v{v.versao} · {dataHoraBR(v.corrigida_em)}</span>{' '}
                era <strong>{brl(Number(v.total))}</strong>
                {Number(v.desconto) > 0 && <span className="text-dim"> (desconto {brl(Number(v.desconto))})</span>}
                {v.corrigida_por_nome && <span className="text-dim"> · por {v.corrigida_por_nome}</span>}
                <span className="text-mute"> — {v.motivo}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
